import camelcaseKeys from 'camelcase-keys'
import { create } from 'zustand'
import { callTool, ToolCallError } from '../lib/tools'
import { isUnknownFieldRefusal } from '../utilities/auditLog'

export type AuditEntry = {
  id: string
  action: string
  actorId?: string
  actorEmail?: string
  targetType?: string
  targetId?: string
  // router#1551. A router before it sends none of these, and every reader falls back.
  targetName?: string
  targetNetworkId?: string
  targetDeleted?: boolean
  reason?: string
  ok: boolean
  code?: string
  createdAt: string
  metadata?: Record<string, string>
  // router#1772 (router v0.4.179): a noBGP staff action, and what admitted it (app#421).
  privileged?: boolean
  supportAccess?: string
}

// The snake_case shape org_audit_list returns (camelcased on read).
type RawEntry = {
  id: string
  action: string
  actor_id?: string
  actor_email?: string
  target_type?: string
  target_id?: string
  target_name?: string
  target_network_id?: string
  target_deleted?: boolean
  reason?: string
  ok: boolean
  code?: string
  created_at: string
  metadata?: Record<string, string>
  privileged?: boolean
  support_access?: string
}

export type AuditQuery = {
  /** Free text over action, actor email, target id and target name (router `q`). */
  q?: string
  /** One action from the menu. */
  action?: string
}

export interface AuditState {
  entries: AuditEntry[]
  loaded: boolean
  hasMore: boolean
  /**
   * True when the router refused `q` (a router before router#1553). The page then loads the log
   * without the text and searches the loaded rows itself, and says so.
   */
  searchUnsupported: boolean
  /**
   * The org's own distinct actions, for the menu (router#1561), or null when the page has none: not
   * asked yet, still loading, or the router refused `include_actions`. Null means the menu uses the
   * static fallback list.
   */
  actions: string[] | null
  /** The org `actions` belongs to, so a switch of org asks again. */
  actionsOrgId: string | null
  loadActions: (orgId: string) => Promise<void>
  set: (state: Partial<AuditState>) => void
  load: (orgId: string, opts?: AuditQuery & { append?: boolean }) => Promise<void>
  reset: () => void
}

const PAGE = 50

// Set once the router refuses `q`, so the next page and the next keystroke do not ask again. A
// reload asks again, which is how the page picks up a router that has since shipped router#1553.
let routerRefusesQ = false

// Set once the router refuses `include_actions` (before router#1561). The menu then keeps the
// static list, and the page does not ask again until a reload.
let routerRefusesIncludeActions = false

// The newest load. A search reloads on each pause in typing, so an older, slower answer must not
// replace the rows of a newer query.
let loadSeq = 0

const state = {
  entries: [],
  loaded: false,
  hasMore: false,
  searchUnsupported: false,
  actions: null,
  actionsOrgId: null,
}

// Page-scoped (loaded by Audit.tsx, not the controller). The audit log is served
// by the router's gated org_audit_list tool (Owner/Admin) — it can't be read via
// PostgREST (no authenticated RLS policy), and actor_email is resolved server-side
// (so ex-members still show). No realtime: an append-only history doesn't need it.
const useAudit = create<AuditState>()((set, get) => ({
  ...state,
  set,
  load: async (orgId, opts = {}) => {
    if (!orgId) return
    const append = opts.append ?? false
    const offset = append ? get().entries.length : 0
    const q = opts.q?.trim() || undefined
    const seq = ++loadSeq
    const list = (withQ: boolean) =>
      callTool<{ entries: RawEntry[] }>('org_audit_list', {
        org_id: orgId,
        // ⚠⚠ `q` ONLY WHEN THERE IS TEXT. A router before router#1553 refuses an unknown field
        // (`DisallowUnknownFields`), so an empty `q` would break the page on that router for a
        // reader who did not search at all.
        q: withQ ? q : undefined,
        action: opts.action || undefined,
        limit: PAGE,
        offset,
      })
    try {
      let res: { entries: RawEntry[] }
      try {
        res = await list(!!q && !routerRefusesQ)
      } catch (e) {
        if (!q || routerRefusesQ || !(e instanceof ToolCallError) || !isUnknownFieldRefusal(e.code, e.message, 'q')) throw e
        routerRefusesQ = true
        res = await list(false)
      }
      if (seq !== loadSeq) return
      const page = (res.entries ?? []).map(e => camelcaseKeys(e)) as AuditEntry[]
      let next = page
      if (append) {
        // Offset pagination can overlap if rows were appended between pages;
        // drop ids we already have so React keys stay unique.
        const seen = new Set(get().entries.map(e => e.id))
        next = [...get().entries, ...page.filter(e => !seen.has(e.id))]
      }
      set({
        entries: next,
        loaded: true,
        hasMore: page.length === PAGE,
        searchUnsupported: !!q && routerRefusesQ,
      })
    } catch (e) {
      console.warn(e)
      if (seq === loadSeq) set({ loaded: true })
    }
  },
  // ⚠ ONE call per org, apart from the page loads. `include_actions` rides on no page or search
  // load: the list does not change with the search, and keeping it apart means a refusal of
  // `include_actions` can never cost the rows, nor a refusal of `q` the menu.
  loadActions: async orgId => {
    if (!orgId || routerRefusesIncludeActions || get().actionsOrgId === orgId) return
    set({ actionsOrgId: orgId, actions: null })
    try {
      const res = await callTool<{ actions?: string[] }>('org_audit_list', {
        org_id: orgId,
        include_actions: true,
        // The router ignores limit for `actions`; 1 keeps the rows it also returns small.
        limit: 1,
      })
      if (get().actionsOrgId !== orgId) return
      // A router that accepted the field but sent no list: keep the fallback.
      set({ actions: Array.isArray(res.actions) ? res.actions : null })
    } catch (e) {
      if (e instanceof ToolCallError && isUnknownFieldRefusal(e.code, e.message, 'include_actions')) {
        routerRefusesIncludeActions = true
      } else {
        console.warn(e)
        // Another failure may pass: let the next visit to the page ask again.
        if (get().actionsOrgId === orgId) set({ actionsOrgId: null })
      }
    }
  },
  reset: () => set({ ...state }),
}))

export default useAudit
